import { useContext, useState } from 'react';
import moment from 'moment';
import { useFlags } from 'launchdarkly-react-client-sdk';
import catchCancel from 'helpers/catchCancel';
import { parseServerError } from 'helpers/errorHelpers';
import featureFlagRenderItem from 'helpers/featureFlagRenderItem';
import { Context } from 'components/Store';
import {
  getUnmappedCropZonesOperationsNumber as cropZoneCountAPI,
  getUnmappedFarms,
  cropZoneMatcher as cropZoneMatcherAPI,
  mappedOperations as mappedOperationsAPI,
  johnDeereMissingPackageWeight as johnDeereMissingPackageWeightAPI,
  getUnmappedCropZonesOperationsNumberV2 as cropZoneCountV2API,
  getUnmappedFarmsV2,
  cropZoneMatcherV2 as cropZoneMatcherV2API,
  mappedOperationsV2 as mappedOperationsV2API,
  johnDeereMissingPackageWeightV2 as johnDeereMissingPackageWeightV2API
} from 'utilities/api';
import { AmplitudeContext } from 'utilities/amplitude/useAmplitude';

const useJohnDeereData = () => {
  const [state, dispatch] = useContext(Context);
  const amplitude = useContext(AmplitudeContext);
  const orgId = state?.organization?.id;

  const {
    releaseFeConnectIntegrationNodeEnable,
    releaseFeConnectIntegrationNodeEnableByOrg
  } = useFlags();

  const isV2Enabled = featureFlagRenderItem(
    releaseFeConnectIntegrationNodeEnable,
    releaseFeConnectIntegrationNodeEnableByOrg,
    orgId
  );

  const cropZoneCountApiSelected = isV2Enabled
    ? cropZoneCountV2API
    : cropZoneCountAPI;
  const getUnmappedFarmsSelected = isV2Enabled
    ? getUnmappedFarmsV2
    : getUnmappedFarms;
  const cropZoneMatcherApiSelected = isV2Enabled
    ? cropZoneMatcherV2API
    : cropZoneMatcherAPI;
  const mappedOperationsApiSelected = isV2Enabled
    ? mappedOperationsV2API
    : mappedOperationsAPI;
  const missingPackageWeightApiSelected = isV2Enabled
    ? johnDeereMissingPackageWeightV2API
    : johnDeereMissingPackageWeightAPI;

  const [unmappedCropZoneCount, setUnmappedCropZoneCount] = useState(undefined);
  const [unmappedFarms, setUnmappedFarms] = useState([]);
  const [since, setSince] = useState(moment().subtract(18, 'months'));
  const [until, setUntil] = useState(moment());
  const [isLoadingCropZoneCount, setIsLoadingCropZoneCount] = useState(false);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [isLoadingMatchOperation, setIsLoadingMatchOperation] = useState(false);
  const [matchOperationError, setMatchOperationError] = useState(false);
  const [isLoadingMappedOperations, setIsLoadingMappedOperations] = useState(
    false
  );
  const [mappedOperations, setMappedOperations] = useState([]);
  const [productsWithoutWeight, setProductsWithoutWeight] = useState([]);
  const [isLoadingWeight, setIsLoadingWeight] = useState(false);
  const loadUnmappedFarms = cropSeasons => {
    setIsLoadingFields(true);
    const { promise } = getUnmappedFarmsSelected.fetch(
      undefined,
      {
        cropSeasons: cropSeasons.toString()
      },
      {
        headers: {
          'cwf-context': JSON.stringify({
            organization_id: orgId
          })
        }
      }
    );

    return promise
      .then(res => {
        setUnmappedFarms(res.data[0]?.farms ?? res.data ?? []);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => setIsLoadingFields(false));
  };

  const loadProductsMissingWeight = () => {
    setIsLoadingWeight(true);
    const { promise } = missingPackageWeightApiSelected.fetch(undefined);

    return promise
      .then(res => {
        const sortedProductsByName = res.data.sort((a, b) => {
          const nameA = a.productName.toUpperCase();
          const nameB = b.productName.toUpperCase();
          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
        setProductsWithoutWeight(sortedProductsByName);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => setIsLoadingWeight(false));
  };

  const loadMappedOperations = () => {
    setIsLoadingMappedOperations(true);
    const { promise } = mappedOperationsApiSelected.fetch(undefined, {
      since: since?.format('YYYY-MM-DD'),
      until: until?.format('YYYY-MM-DD')
    });
    return promise
      .then(res => {
        const _operations = res.data;
        let _sourceFields = _operations.reduce((accumulator, operation) => {
          const field = operation?.fields?.[0];
          if (!field) return accumulator;
          if (accumulator[field.id]) {
            accumulator[field.id].operations.push(operation);
          } else {
            accumulator[field.id] = {
              fieldId: field.id,
              fieldName: field.description,
              farmName: operation.farms[0].description,
              operations: [operation]
            };
          }
          return accumulator;
        }, {});
        _sourceFields = Object.values(_sourceFields)
          .sort(({ fieldName: fieldA }, { fieldName: fieldB }) =>
            fieldA.localeCompare(fieldB)
          )
          .sort(
            ({ operations: opsA }, { operations: opsB }) =>
              opsB.length - opsA.length
          );
        setMappedOperations(_sourceFields);
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => setIsLoadingMappedOperations(false));
  };

  const loadUnmappedCropZonesCount = id => {
    setIsLoadingCropZoneCount(true);
    const { promise } = cropZoneCountApiSelected.fetch(id, undefined, {
      headers: {
        'cwf-context': JSON.stringify({
          organization_id: orgId
        })
      }
    });

    return promise
      .then(({ data }) => {
        amplitude.sendEventToAmplitude(
          amplitude.events.epic.Integrations.JohnDeere
            .numberOfOperationAvailable,
          { operations: data?.unmappedCount ?? data?.count ?? 0 }
        );
        setUnmappedCropZoneCount({
          unmappedCount: data?.unmappedCount ?? data?.count
        });
      })
      .catch(catchCancel)
      .catch(err => {
        parseServerError(dispatch)(err);
      })
      .finally(() => setIsLoadingCropZoneCount(false));
  };

  /**
   * Handle the crop zones matches in bulk.
   *
   * @param {Array} operations - An array of operation objects.
   * @param {number} operations[].operationId - The ID of the operation.
   * @param {number} operations[].cropzoneId - The ID of the crop zone.
   * @param {number} operations[].cropseasonId - The ID of the crop season.
   * @param {string} operations[].creationDate - The creation date of the operation.
   * @returns {Promise<void>} A promise that resolves when the crop zones are loaded and matched.
   */
  const loadMatchCropZone = (operations = []) => {
    setIsLoadingMatchOperation(true);
    const { promise } = cropZoneMatcherApiSelected.post(operations);
    return promise
      .then(response => {
        return response;
      })
      .catch(catchCancel)
      .catch(err => {
        setMatchOperationError(true);
        return err.response;
      })
      .finally(() => setIsLoadingMatchOperation(false));
  };

  return {
    setSince,
    since,
    setUntil,
    until,
    isLoadingFields,
    loadUnmappedCropZonesCount,
    isLoadingCropZoneCount,
    unmappedCropZoneCount,
    unmappedFarms,
    loadUnmappedFarms,
    setUnmappedCropZoneCount,
    isLoadingMatchOperation,
    loadMatchCropZone,
    matchOperationError,
    setMatchOperationError,
    loadMappedOperations,
    isLoadingMappedOperations,
    mappedOperations,
    loadProductsMissingWeight,
    isLoadingWeight,
    productsWithoutWeight
  };
};

export default useJohnDeereData;
